import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { statisticAPI } from "api/stats-api";
import cn from "classnames";

import {
  setPopupStateAC,
  setRedirectItemAC,
  setSidebarBlockStatusAC,
  triggerRedirectAC,
} from "actions-thunks/actions";
import { authThunks } from "actions-thunks/auth-thunks";
import { accountThunks } from "actions-thunks/account-thunks";
import { utilityFunctions } from "ui/utilities/utilityFunctions";

import "./main-taskbar.scss";

const MainTaskbar = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { currentAccount, extensions } = useSelector((state) => state.accounts);
  const { triggerRedirect, currentPageTitle, sidebarBlockCollapsed } =
    useSelector((state) => state.utils);
  const { burgerMenuOpened, fieldIsEditing } = useSelector(
    (state) => state.profile
  );

  const blockRef = useRef(null);
  const menuBlockRef = useRef(null);

  const [isToggled, setIsToggled] = useState(false);
  const [localExtensions, setLocalExtension] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onLogoutClick = () => {
    currentAccount?._id &&
      statisticAPI.sendStatUsage(currentAccount._id, { page: `Log Out` });
    if (fieldIsEditing.status) {
      dispatch(
        setPopupStateAC({
          opened: true,
          message:
            "Are you sure you want to logout without saving the changes ?",
          type: "saves",
        })
      );
      dispatch(setRedirectItemAC({ type: "logout", item: null }));
    } else {
      dispatch(authThunks.logoutTC());
    }
  };

  const handleClickOutside = (event) => {
    if (
      blockRef.current &&
      !blockRef.current.contains(event.target) &&
      menuBlockRef.current &&
      !menuBlockRef.current.contains(event.target) &&
      isToggled
    ) {
      onDropdownToggleClick();
    }
  };

  useEffect(() => {
    if (currentAccount) {
      dispatch(accountThunks.setAccountExtensionsTC(currentAccount._id));
    }
  }, [dispatch, currentAccount]);

  useEffect(() => {
    if (extensions) {
      setLocalExtension(
        extensions.find((e) => e.code === "summary/balance") || { noData: true }
      );
    }
  }, [extensions]);

  const setDateFormat = useMemo(() => {
    if (localExtensions) {
      return localExtensions.noData
        ? "N/A"
        : utilityFunctions.transformToStepDateView(localExtensions.date || "");
    }
    return "N/A";
  }, [localExtensions]);

  useEffect(() => {
    if (isToggled) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToggled]);

  useEffect(() => {
    if (triggerRedirect.status) {
      navigate(triggerRedirect.direction);
      dispatch(triggerRedirectAC({ direction: "", status: false }));
    }
  }, [triggerRedirect, navigate, dispatch]);

  const onDropdownToggleClick = (e) => {
    if (!!isToggled) {
      setIsToggled(false);
    } else {
      setIsToggled(e);
    }
  };

  const onBurgerMenuClick = () => {
    dispatch(setSidebarBlockStatusAC(!sidebarBlockCollapsed));
  };

  const firstname = currentAccount?.personal.firstName || "";
  const lastname = currentAccount?.personal.lastName || "";

  const accountTypeValue = currentAccount?.accountType
    ?.replace("zimtra", "")
    ?.replace("expert", "intermediate");

  const formattedAccountType = accountTypeValue
    ? accountTypeValue.charAt(0).toUpperCase() + accountTypeValue.slice(1)
    : "";

  const isSimulator =
    !formattedAccountType && currentAccount?.softwareType === "Sim"
      ? "Simulator"
      : "";

  return (
    <div className="taskbar-block" data-testid="main-taskbar">
      <div className="taskbar-container">
        {isAuthenticated && (
          <img
            onClick={onBurgerMenuClick}
            className="menu-icon"
            id="sidebar-burger-menu-icon"
            width={24}
            height={24}
            src={`${process.env.PUBLIC_URL}/${
              burgerMenuOpened ? "x" : "menu"
            }-icon.svg`}
            alt="menu-icon"
          />
        )}
        <div className="taskbar-title-block">
          {currentPageTitle && (
            <h3 className="taskbar-page-title">{currentPageTitle}</h3>
          )}
          <span>updated on {setDateFormat}</span>
        </div>
        <div
          id="taskbar-collapse-block"
          className={cn(
            "taskbar-collapse-block",
            `${accountTypeValue || "sim"}-acc`
          )}
        >
          <div
            className="taskbar-collapse-block-item"
            ref={blockRef}
            onClick={onDropdownToggleClick}
          >
            <div className="taskbar-avatar" id="taskbar-avatar">
              <div className="taskbar-avatar-letters">
                {firstname[0] && `${firstname[0]}${lastname[0]}`.toUpperCase()}
              </div>
            </div>
            <span>
              {formattedAccountType}
              {isSimulator}
            </span>
          </div>
          {isToggled && (
            <div
              className="menu-block"
              id="logout-item"
              onClick={onLogoutClick}
              ref={menuBlockRef}
            >
              <img
                width="24"
                height="24"
                src={`${process.env.PUBLIC_URL}/cabinet-logout.svg`}
                alt="logout profile"
              />
              Logout
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MainTaskbar;
